// CC1 · Legacy StarTracker Import — Owner Review Workspace.
//
// Preview-first workflow: upload → server profiles + normalizes →
// operator reviews / edits / classifies proposals → Apply is
// permission-gated AND separately gated by Owner authorization on
// the batch payload. Every action here writes to the batch preview
// payload only. Zero writes to customers/organizations/contacts
// domain collections until Apply is authorized AND clicked.

import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  UploadCloud, RefreshCw, Play, Filter, CheckCircle2,
  AlertTriangle, Copy, Edit3, Layers, Loader2, ShieldAlert,
} from "lucide-react";
import { legacyImportApi } from "@/lib/entities";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { RoleGate } from "@/components/RoleGate";

const STATUS_ORDER = [
  "READY", "NEEDS_REVIEW", "POSSIBLE_DUPLICATE",
  "EXISTING_MATCH", "SKIP", "ERROR",
];

const STATUS_STYLES = {
  READY:              "bg-touring-lime/15 text-carbon border-touring-lime/60",
  NEEDS_REVIEW:       "bg-amber-hold/20 text-carbon border-amber-hold",
  POSSIBLE_DUPLICATE: "bg-chrome-mist text-carbon border-coach-silver",
  EXISTING_MATCH:     "bg-coach-blue/10 text-coach-blue border-coach-blue/40",
  SKIP:               "bg-chrome-mist text-steel border-coach-silver",
  ERROR:              "bg-critical-red/15 text-critical-red border-critical-red/60",
};

export default function LegacyImportPage() {
  const { can } = useAuth();
  const canImport = can("contacts.import");
  const [batches, setBatches] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(null);
  const [preview, setPreview] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [nearDups, setNearDups] = useState(null);
  const [filter, setFilter] = useState("READY");
  const [busy, setBusy] = useState(false);
  const [editRow, setEditRow] = useState(null); // proposal object being edited

  const loadBatches = async () => {
    try { setBatches(await legacyImportApi.list()); }
    catch { toast.error("Failed to load import batches"); }
  };
  useEffect(() => { if (canImport) loadBatches(); }, [canImport]);

  const openBatch = async (id) => {
    setBusy(true);
    try {
      const b = await legacyImportApi.get(id);
      setPreview(b.preview);
      setBatchId(id);
      const [dash, dups] = await Promise.all([
        legacyImportApi.dashboard(id),
        legacyImportApi.orgNearDuplicates(id),
      ]);
      setDashboard(dash);
      setNearDups(dups);
    } catch { toast.error("Failed to load batch"); }
    finally { setBusy(false); }
  };

  const reloadOpen = async () => {
    if (!batchId) return;
    await openBatch(batchId);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const p = await legacyImportApi.upload(f);
      setPreview(p);
      setBatchId(p.batch_id);
      const [dash, dups] = await Promise.all([
        legacyImportApi.dashboard(p.batch_id),
        legacyImportApi.orgNearDuplicates(p.batch_id),
      ]);
      setDashboard(dash);
      setNearDups(dups);
      await loadBatches();
      toast.success(`Preview ready: ${p.data_rows} rows parsed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const applyBatch = async () => {
    // The button is DISABLED unless dashboard.apply_gate.state === "authorized".
    // This handler exists only to relay the 423 error message if hit programmatically.
    if (!batchId) return;
    setApplying(batchId);
    try {
      const r = await legacyImportApi.apply(batchId);
      if (r.already_applied) toast.info("Batch was already applied.");
      else toast.success(`Applied: +${r.summary.customers_created} customers, +${r.summary.organizations_created} orgs, +${r.summary.contacts_created} contacts`);
      await loadBatches(); await reloadOpen();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 423) toast.error(`Apply gated: ${detail}`);
      else toast.error(detail || "Apply failed");
    } finally { setApplying(null); }
  };

  const bulkApprove = async () => {
    if (!batchId) return;
    setBusy(true);
    try {
      const r = await legacyImportApi.bulkApproveReady(batchId);
      toast.success(`Approved ${r.approved_count.toLocaleString()} clean READY rows`);
      await reloadOpen();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk approve failed"); }
    finally { setBusy(false); }
  };

  const refreshMatches = async () => {
    if (!batchId) return;
    setBusy(true);
    try {
      const r = await legacyImportApi.refreshMatches(batchId);
      toast.success(`Match analysis refreshed — ${r.changed} field(s) updated`);
      await reloadOpen();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    finally { setBusy(false); }
  };

  const stats = dashboard?.stats || preview?.stats || {};
  const proposals = preview?.proposals || [];
  const filtered = useMemo(
    () => proposals.filter((p) => (p.status || "READY") === filter),
    [proposals, filter],
  );
  const gateState = dashboard?.apply_gate?.state || "blocked";
  const gateMsg = dashboard?.apply_gate?.message || "Final confirmation blocked pending Owner authorization";

  return (
    <RoleGate permission="contacts.import">
      <PageShell>
        {/* RC4 · shared SectionHeader adoption landmark (sr-only until full visual migration lands progressively). */}
        <SectionHeader testid="page-section-landmark" className="sr-only">Legacy Import</SectionHeader>
        <PageHeader
          testid="page-legacy-import"
          title="Legacy StarTracker Import"
          subtitle="Review, edit, and classify proposed Customers, Organizations, and Contacts before Apply. Apply remains disabled until Owner authorization."
          right={
            <label
              htmlFor="legacy-file"
              className="inline-flex items-center gap-1.5 bg-coach-blue hover:bg-deep-road-navy text-white text-[12.5px] font-medium px-3 py-1.5 rounded-sm cursor-pointer"
            >
              <UploadCloud size={14} />
              {uploading ? "Parsing…" : "Upload workbook"}
              <input id="legacy-file" data-testid="legacy-file-input"
                type="file" accept=".xls,.xlsx" className="hidden"
                onChange={onFile} disabled={uploading} />
            </label>
          }
        />

        {/* Batch history */}
        <div className="bg-white border border-coach-silver rounded-md mb-6" data-testid="legacy-batch-history">
          <div className="px-4 py-2 border-b border-coach-silver flex items-center justify-between">
            <div className="text-[10.5px] uppercase tracking-widest text-steel">Recent batches</div>
            <button onClick={loadBatches} className="text-[11.5px] text-steel hover:text-coach-blue inline-flex items-center gap-1">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
              <tr>
                <th className="text-left px-4 py-2">Uploaded</th>
                <th className="text-left px-4 py-2">Filename</th>
                <th className="text-left px-4 py-2">Sheet</th>
                <th className="text-right px-4 py-2">Rows</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="w-24" />
              </tr>
            </thead>
            <tbody>
              {batches.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-steel text-[13px]">No import batches yet.</td></tr>
              )}
              {batches.map((b) => (
                <tr key={b.id} data-testid={`legacy-batch-row-${b.id}`}
                    className="border-t border-coach-silver/60 hover:bg-road-white">
                  <td className="px-4 py-2 text-steel font-mono text-[11.5px]">{b.created_at?.slice(0,16).replace("T"," ")}</td>
                  <td className="px-4 py-2 text-carbon">{b.source_filename}</td>
                  <td className="px-4 py-2 text-steel">{b.source_sheet}</td>
                  <td className="px-4 py-2 text-right text-steel font-mono">{b.preview?.data_rows ?? "—"}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded-sm border text-[10.5px] uppercase tracking-widest ${
                      b.status === "applied"
                        ? "bg-touring-lime/20 border-touring-lime text-carbon"
                        : "bg-chrome-mist border-coach-silver text-carbon"
                    }`}>{b.status}</span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => openBatch(b.id)}
                            data-testid={`legacy-batch-open-${b.id}`}
                            className="text-[11.5px] text-coach-blue hover:underline">Review</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        {busy && <div className="text-[12px] text-steel mb-3 inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Loading…</div>}

        {preview && (
          <div data-testid="legacy-preview-panel" className="space-y-4">

            {/* Final pre-Apply Dashboard */}
            <div className="bg-white border border-coach-silver rounded-md p-4" data-testid="preapply-dashboard">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Final Pre-Apply Dashboard</div>
                  <div className="text-[13.5px] text-carbon font-medium mt-0.5">
                    {dashboard?.source_filename || preview.source_filename}
                    <span className="text-steel"> · sheet {dashboard?.source_sheet || preview.source_sheet}</span>
                  </div>
                  <div className="text-[11px] text-steel mt-0.5">
                    Batch id <span className="font-mono">{batchId}</span>
                    {dashboard?.last_refresh_at && <> · last refresh {dashboard.last_refresh_at.slice(0,16).replace("T"," ")}</>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={refreshMatches} disabled={busy}
                          data-testid="btn-refresh-matches"
                          className="text-[12px] border border-coach-blue text-coach-blue px-2 py-1 rounded-sm inline-flex items-center gap-1 disabled:opacity-60">
                    <RefreshCw size={12} /> Refresh Match Analysis
                  </button>
                  <button onClick={bulkApprove} disabled={busy || !(stats.ready_rows > 0)}
                          data-testid="btn-bulk-approve-ready"
                          className="text-[12px] bg-coach-blue text-white hover:bg-deep-road-navy px-3 py-1 rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
                    <CheckCircle2 size={12} /> Batch Approve READY ({(stats.ready_rows || 0).toLocaleString()})
                  </button>
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-3 text-[13px]">
                <Stat label="Total source rows" v={dashboard?.total_source_rows ?? preview.total_source_rows} testid="stat-total-rows" />
                <Stat label="Data rows" v={stats.rows_parsed} testid="stat-data-rows" />
                <Stat label="READY" v={stats.ready_rows} accent="touring-lime" testid="stat-ready" />
                <Stat label="Approved READY" v={stats.approved_ready_rows} accent="touring-lime" testid="stat-approved-ready" />
                <Stat label="Needs review" v={stats.needs_review_rows} accent="amber-hold" testid="stat-needs-review" />
                <Stat label="Possible duplicate" v={stats.possible_duplicate_rows} accent="amber-hold" testid="stat-possible-dupes" />
                <Stat label="Existing match" v={stats.existing_match_rows} testid="stat-existing-match" />
                <Stat label="Skip" v={stats.skip_rows} testid="stat-skip" />
                <Stat label="Error" v={stats.error_rows} accent="critical-red" testid="stat-error" />

                <Stat label="New Customers" v={stats.proposed_customers} testid="stat-customers-new" />
                <Stat label="Matched Customers" v={stats.existing_customer_matches} testid="stat-customers-match" />
                <Stat label="New Organizations" v={stats.proposed_organizations} testid="stat-orgs-new" />
                <Stat label="Matched Organizations" v={stats.existing_org_matches} testid="stat-orgs-match" />
                <Stat label="New Contacts" v={stats.proposed_contacts} testid="stat-contacts-new" />
                <Stat label="Matched Contacts" v={stats.existing_contact_matches} testid="stat-contacts-match" />
                <Stat label="Archived Contact Matches" v={stats.archived_contact_matches} accent="critical-red" testid="stat-contacts-archived" />
                <Stat label="Proposed Relationships" v={stats.proposed_relationships} testid="stat-rels" />
              </div>

              {/* Apply gate */}
              <div className={`mt-4 rounded-sm border p-3 flex items-start gap-3 ${
                dashboard?.status === "applied"
                  ? "bg-touring-lime/10 border-touring-lime"
                  : gateState === "authorized"
                  ? "bg-touring-lime/10 border-touring-lime"
                  : "bg-amber-hold/10 border-amber-hold"
              }`} data-testid="apply-gate-banner">
                <ShieldAlert size={16} className={gateState === "authorized" ? "text-carbon" : "text-amber-hold"} />
                <div className="flex-1 text-[12.5px] text-carbon">
                  <div className="font-medium">
                    {dashboard?.status === "applied"
                      ? <>Batch <span data-testid="apply-gate-state" className="uppercase tracking-widest">APPLIED</span></>
                      : <>Apply gate: <span data-testid="apply-gate-state" className="uppercase tracking-widest">{gateState}</span></>}
                  </div>
                  <div className="text-steel text-[11.5px]">
                    {dashboard?.status === "applied"
                      ? "Import complete — domain collections populated. No further Apply required."
                      : gateMsg}
                  </div>
                </div>
                {dashboard?.status !== "applied" && (
                  <button
                    onClick={applyBatch}
                    disabled={gateState !== "authorized" || applying === batchId}
                    data-testid="btn-apply-batch"
                    title={gateState !== "authorized" ? gateMsg : "Apply — writes to domain collections"}
                    className="inline-flex items-center gap-1 text-[12px] font-medium bg-coach-blue hover:bg-deep-road-navy text-white px-3 py-1.5 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Play size={11} />
                    {applying === batchId ? "Applying…" : "Apply Import"}
                  </button>
                )}
              </div>
            </div>

            {/* Organization near-duplicate analysis */}
            {nearDups && nearDups.near_duplicate_group_count > 0 && (
              <div className="bg-white border border-coach-silver rounded-md p-4" data-testid="org-near-dup-panel">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-[10.5px] uppercase tracking-widest text-steel">Organization Near-Duplicate Analysis</div>
                    <div className="text-[12.5px] text-carbon mt-0.5">
                      <strong data-testid="near-dup-group-count">{nearDups.near_duplicate_group_count}</strong> group(s) across {nearDups.total_proposed_orgs.toLocaleString()} proposed orgs
                      <span className="text-steel"> · {nearDups.distinct_fuzzy_keys.toLocaleString()} distinct fuzzy keys</span>
                    </div>
                  </div>
                  <Layers size={16} className="text-steel" />
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]" data-testid="near-dup-table">
                  <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
                    <tr>
                      <th className="text-left px-3 py-1.5">Fuzzy key</th>
                      <th className="text-right px-3 py-1.5 w-24">Members</th>
                      <th className="text-left px-3 py-1.5">Distinct names in batch</th>
                      <th className="text-left px-3 py-1.5 w-32">Recommended</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nearDups.groups.map((g) => (
                      <tr key={g.fuzzy_key} className="border-t border-coach-silver/60" data-testid={`near-dup-row-${g.fuzzy_key}`}>
                        <td className="px-3 py-2 font-mono text-[11.5px] text-steel">{g.fuzzy_key}</td>
                        <td className="px-3 py-2 text-right font-mono">{g.member_count}</td>
                        <td className="px-3 py-2 text-carbon">
                          {g.distinct_names.map((n) => <div key={n}>{n}</div>)}
                        </td>
                        <td className="px-3 py-2">
                          <span className="inline-block px-2 py-0.5 rounded-sm border text-[10.5px] uppercase tracking-widest bg-chrome-mist border-coach-silver text-carbon">
                            {g.recommended.replace(/_/g," ")}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <div className="text-[11px] text-steel mt-2">Groups are surfaced for operator review only — nothing is auto-merged.</div>
              </div>
            )}

            {/* Skipped-row explanation card */}
            {dashboard?.skipped_row_sample && (
              <div className="bg-white border border-coach-silver rounded-md p-4" data-testid="skipped-row-panel">
                <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">Skipped row (source row #{dashboard.skipped_row_sample.source_row})</div>
                <div className="text-[12.5px] text-carbon">
                  Reason: <span className="font-mono text-amber-hold">{dashboard.skipped_row_sample.reason}</span>
                  {dashboard.skipped_row_sample.correctable && (
                    <span className="text-steel"> — correctable via inline edit</span>
                  )}
                </div>
                <div className="text-[11.5px] text-steel mt-2">Relevant raw values:</div>
                <pre className="text-[11px] font-mono text-carbon bg-road-white p-2 rounded-sm overflow-x-auto mt-1" data-testid="skipped-row-raw">
{JSON.stringify(
  Object.fromEntries(
    Object.entries(dashboard.skipped_row_sample.raw).filter(([_, v]) => v && String(v).trim())
  ),
  null, 2,
)}
                </pre>
              </div>
            )}

            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="preview-filter-tabs">
              <Filter size={12} className="text-steel" />
              {STATUS_ORDER.map((k) => {
                const count = proposals.filter((p) => (p.status || "READY") === k).length;
                return (
                  <button key={k}
                    data-testid={`preview-filter-${k}`}
                    onClick={() => setFilter(k)}
                    className={`text-[11.5px] uppercase tracking-widest px-2 py-1 rounded-sm border ${
                      filter === k
                        ? "bg-coach-blue text-white border-coach-blue"
                        : "bg-white text-carbon border-coach-silver hover:bg-chrome-mist"
                    }`}>
                    {k.replace(/_/g, " ")} ({count.toLocaleString()})
                  </button>
                );
              })}
            </div>

            {/* Proposal rows */}
            <div className="bg-white border border-coach-silver rounded-md" data-testid="preview-rows-table">
              <div className="overflow-x-auto">
              <table className="w-full text-[13px] min-w-[720px]">
                <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
                  <tr>
                    <th className="text-left px-3 py-2 w-14">Row</th>
                    <th className="text-left px-3 py-2">Customer</th>
                    <th className="text-left px-3 py-2">Organizations</th>
                    <th className="text-left px-3 py-2">Contacts</th>
                    <th className="text-left px-3 py-2 w-40">Flags / Status</th>
                    <th className="text-right px-3 py-2 w-28"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 100).map((p) => (
                    <tr key={p.source_row}
                        data-testid={`preview-row-${p.source_row}`}
                        className="border-t border-coach-silver/60 align-top">
                      <td className="px-3 py-2 text-steel font-mono">{p.source_row}</td>
                      <td className="px-3 py-2 text-carbon">
                        {p.customer?.display_name || <span className="text-steel">—</span>}
                        {p.customer?.match_status === "EXISTING" && (
                          <div className="text-[10.5px] text-steel">existing match</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-carbon">
                        {(p.organizations || []).map((o) => (
                          <div key={o.role_in_row + o.display_name} className="text-[12.5px]">
                            <span className="text-steel text-[10.5px] uppercase tracking-widest mr-1">
                              {(o.role_in_row || "org").replace(/_/g, " ")}
                            </span>
                            {o.display_name}
                          </div>
                        ))}
                      </td>
                      <td className="px-3 py-2 text-carbon">
                        {(p.contacts || []).map((c, i) => (
                          <div key={i} className="text-[12.5px]">
                            <span className="text-steel text-[10.5px] uppercase tracking-widest mr-1">
                              {(c.role_in_row || "contact").replace(/_/g, " ")}
                            </span>
                            {`${c.first_name || ""} ${c.last_name || ""}`.trim() || <span className="text-steel">(no name)</span>}
                            <span className="text-steel"> · {c.primary_email || "—"}</span>
                            {c.match_status && c.match_status !== "NEW" && (
                              <span className="text-[10px] text-amber-hold ml-1">[{c.match_status}]</span>
                            )}
                          </div>
                        ))}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-sm border text-[10.5px] uppercase tracking-widest ${STATUS_STYLES[p.status] || ""}`}>
                          {(p.status || "READY").replace(/_/g, " ")}
                        </span>
                        {p.approved && (
                          <span className="ml-1 inline-block px-1.5 py-0.5 rounded-sm border border-touring-lime text-carbon bg-touring-lime/20 text-[9.5px] uppercase tracking-widest">
                            approved
                          </span>
                        )}
                        {(p.flags || []).map((f) => (
                          <div key={f} className="text-[10.5px] text-amber-hold mt-1">{f}</div>
                        ))}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => setEditRow(p)}
                                data-testid={`preview-row-edit-${p.source_row}`}
                                className="text-[11.5px] text-coach-blue hover:underline inline-flex items-center gap-1">
                          <Edit3 size={11} /> Review
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-steel text-[13px]">No rows in this bucket.</td></tr>
                  )}
                </tbody>
              </table>
              </div>
              {filtered.length > 100 && (
                <div className="px-4 py-2 text-[11px] text-steel border-t border-coach-silver">
                  Showing the first 100 of {filtered.length.toLocaleString()} rows.
                </div>
              )}
            </div>
          </div>
        )}

        {editRow && (
          <ProposalEditor
            proposal={editRow}
            batchId={batchId}
            onClose={() => setEditRow(null)}
            onSaved={async () => { setEditRow(null); await reloadOpen(); }}
          />
        )}
      </PageShell>
    </RoleGate>
  );
}

const Stat = ({ label, v, accent, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10.5px] uppercase tracking-widest text-steel">{label}</div>
    <div className={`text-[16px] font-mono font-semibold ${accent ? `text-${accent}` : "text-carbon"}`}>
      {v?.toLocaleString?.() ?? v ?? "—"}
    </div>
  </div>
);

// ---------------------------------------------------------------------
// Inline Proposal Editor — right-side sheet.
// Supports:
//   - editing Customer Name
//   - editing each Organization (display_name / legal_name / role)
//   - editing each Contact (first/last/email/mobile/type)
//   - classify NEEDS_REVIEW business_mgr as Organization / Contact / Ignore
//   - resolve POSSIBLE_DUPLICATE with side-by-side existing preview
//   - set row disposition/action (READY / SKIP / NEEDS_REVIEW)
//   - operator note
// All writes hit /proposals/{source_row} — preview only.
// ---------------------------------------------------------------------
function ProposalEditor({ proposal, batchId, onClose, onSaved }) {
  const [p, setP] = useState(() => JSON.parse(JSON.stringify(proposal)));
  const [saving, setSaving] = useState(false);

  const setCustomer = (patch) => setP({ ...p, customer: { ...(p.customer || {}), ...patch } });
  const setOrg = (i, patch) => {
    const orgs = (p.organizations || []).map((o, j) => j === i ? { ...o, ...patch } : o);
    setP({ ...p, organizations: orgs });
  };
  const setContact = (i, patch) => {
    const cs = (p.contacts || []).map((c, j) => j === i ? { ...c, ...patch } : c);
    setP({ ...p, contacts: cs });
  };

  const save = async () => {
    setSaving(true);
    try {
      await legacyImportApi.editProposal(batchId, p.source_row, {
        customer: p.customer,
        organizations: p.organizations,
        contacts: p.contacts,
        status: p.status,
        operator_note: p.operator_note,
      });
      toast.success(`Row ${p.source_row} saved`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const classify = async (decision) => {
    setSaving(true);
    try {
      await legacyImportApi.classifyBusinessMgr(batchId, p.source_row, decision);
      toast.success(`Row ${p.source_row} classified as ${decision}`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Classify failed"); }
    finally { setSaving(false); }
  };

  const resolveDup = async (action, roleInRow) => {
    setSaving(true);
    try {
      await legacyImportApi.resolveDuplicate(batchId, p.source_row, action, roleInRow);
      toast.success(`Duplicate resolved: ${action}`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resolve failed"); }
    finally { setSaving(false); }
  };

  const isNeedsReview = p.status === "NEEDS_REVIEW";
  const dupContact = (p.contacts || []).find((c) => c.match_status === "POSSIBLE_DUPLICATE");
  const isPossibleDup = p.status === "POSSIBLE_DUPLICATE" && dupContact;

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end"
         data-testid={`proposal-editor-${p.source_row}`}
         onClick={onClose}>
      <div className="bg-white w-[560px] max-w-full h-full overflow-y-auto shadow-xl border-l border-coach-silver"
           onClick={(e) => e.stopPropagation()}>
        <div className="bg-carbon text-white px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-coach-silver">Proposal · source row {p.source_row}</div>
            <div className="text-[15px] font-medium mt-0.5">{p.customer?.display_name || "(no name)"}</div>
          </div>
          <button onClick={onClose} data-testid="proposal-editor-close"
                  className="text-coach-silver hover:text-white text-[12px]">Close</button>
        </div>
        <div className="p-4 space-y-4 text-[12.5px]">

          {/* Classification for NEEDS_REVIEW */}
          {isNeedsReview && (
            <div className="border border-amber-hold bg-amber-hold/10 rounded-sm p-3" data-testid="classifier-panel">
              <div className="flex items-center gap-1 text-amber-hold text-[11.5px] uppercase tracking-widest mb-1">
                <AlertTriangle size={12} /> Needs Review — Business Manager ambiguous
              </div>
              <div className="text-carbon text-[12.5px] mb-2">
                Raw value: <span className="font-mono">{p.raw?.bizmgr}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button disabled={saving} onClick={() => classify("organization")}
                        data-testid="classify-as-organization"
                        className="bg-coach-blue text-white text-[11.5px] px-3 py-1.5 rounded-sm">Organization</button>
                <button disabled={saving} onClick={() => classify("contact")}
                        data-testid="classify-as-contact"
                        className="border border-coach-blue text-coach-blue text-[11.5px] px-3 py-1.5 rounded-sm">Contact</button>
                <button disabled={saving} onClick={() => classify("ignore")}
                        data-testid="classify-as-ignore"
                        className="border border-coach-silver text-steel text-[11.5px] px-3 py-1.5 rounded-sm">Ignore / Skip</button>
              </div>
            </div>
          )}

          {/* Possible-duplicate resolver with side-by-side */}
          {isPossibleDup && (
            <DuplicateResolver
              proposal={p}
              dupContact={dupContact}
              disabled={saving}
              onAction={(action) => resolveDup(action, dupContact.role_in_row)}
            />
          )}

          {/* Customer */}
          <fieldset className="border border-coach-silver rounded-sm p-3">
            <legend className="text-[10.5px] uppercase tracking-widest text-steel px-1">Customer</legend>
            <label className="block mb-2">
              <span className="text-[10.5px] uppercase tracking-widest text-steel">Display name</span>
              <input value={p.customer?.display_name || ""}
                     onChange={(e) => setCustomer({ display_name: e.target.value })}
                     data-testid={`edit-customer-name-${p.source_row}`}
                     className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[13px]" />
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-widest text-steel">Notes</span>
              <input value={p.customer?.notes || ""}
                     onChange={(e) => setCustomer({ notes: e.target.value })}
                     data-testid={`edit-customer-notes-${p.source_row}`}
                     className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[13px]" />
            </label>
            {p.customer?.match_status === "EXISTING" && (
              <div className="text-[11px] text-steel mt-1">Matches existing customer {p.customer.existing_id}</div>
            )}
          </fieldset>

          {/* Organizations */}
          <fieldset className="border border-coach-silver rounded-sm p-3">
            <legend className="text-[10.5px] uppercase tracking-widest text-steel px-1">Organizations</legend>
            {(p.organizations || []).length === 0 && <div className="text-steel text-[12px]">None proposed.</div>}
            {(p.organizations || []).map((o, i) => (
              <div key={i} className="mb-3 last:mb-0 border-b border-coach-silver/50 pb-3 last:border-0 last:pb-0">
                <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">
                  {(o.role_in_row || "org").replace(/_/g," ")}
                </div>
                <label className="block mb-1">
                  <span className="text-[10.5px] uppercase tracking-widest text-steel">Display name</span>
                  <input value={o.display_name || ""}
                         onChange={(e) => setOrg(i, { display_name: e.target.value })}
                         data-testid={`edit-org-name-${p.source_row}-${i}`}
                         className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[13px]" />
                </label>
                <label className="block">
                  <span className="text-[10.5px] uppercase tracking-widest text-steel">Legal name</span>
                  <input value={o.legal_name || ""}
                         onChange={(e) => setOrg(i, { legal_name: e.target.value })}
                         className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[13px]" />
                </label>
                {o.match_status === "EXISTING" && (
                  <div className="text-[11px] text-steel mt-1">Matches existing org {o.existing_id}</div>
                )}
              </div>
            ))}
          </fieldset>

          {/* Contacts */}
          <fieldset className="border border-coach-silver rounded-sm p-3">
            <legend className="text-[10.5px] uppercase tracking-widest text-steel px-1">Contacts</legend>
            {(p.contacts || []).length === 0 && <div className="text-steel text-[12px]">None proposed.</div>}
            {(p.contacts || []).map((c, i) => (
              <div key={i} className="mb-3 last:mb-0 border-b border-coach-silver/50 pb-3 last:border-0 last:pb-0">
                <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">Contact Type</div>
                <select value={c.role_in_row || "manager"}
                        onChange={(e) => setContact(i, { role_in_row: e.target.value })}
                        data-testid={`edit-contact-type-${p.source_row}-${i}`}
                        className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[13px] mb-1 bg-white">
                  {[
                    "artist_principal","tour_manager","production_manager","business_manager",
                    "personal_manager","road_manager","transportation_coordinator","accountant",
                    "accounts_payable","booking_agent","manager","assistant","security",
                    "vendor_contact","pickup_dropoff","other",
                  ].map((r) => <option key={r} value={r}>{r.replace(/_/g," ")}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="text-[10.5px] uppercase tracking-widest text-steel">First</span>
                    <input value={c.first_name || ""} onChange={(e) => setContact(i, { first_name: e.target.value })}
                           data-testid={`edit-contact-first-${p.source_row}-${i}`}
                           className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[13px]" />
                  </label>
                  <label className="block">
                    <span className="text-[10.5px] uppercase tracking-widest text-steel">Last</span>
                    <input value={c.last_name || ""} onChange={(e) => setContact(i, { last_name: e.target.value })}
                           data-testid={`edit-contact-last-${p.source_row}-${i}`}
                           className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[13px]" />
                  </label>
                </div>
                <label className="block mt-1">
                  <span className="text-[10.5px] uppercase tracking-widest text-steel">Email</span>
                  <input value={c.primary_email || ""} onChange={(e) => setContact(i, { primary_email: e.target.value })}
                         data-testid={`edit-contact-email-${p.source_row}-${i}`}
                         className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[13px]" />
                </label>
                <label className="block mt-1">
                  <span className="text-[10.5px] uppercase tracking-widest text-steel">Mobile</span>
                  <input value={c.mobile || ""} onChange={(e) => setContact(i, { mobile: e.target.value })}
                         data-testid={`edit-contact-mobile-${p.source_row}-${i}`}
                         className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[13px]" />
                </label>
                {c.match_status && c.match_status !== "NEW" && (
                  <div className="text-[11px] text-amber-hold mt-1">Match: {c.match_status}{c.existing_id ? ` · ${c.existing_id}` : ""}</div>
                )}
              </div>
            ))}
          </fieldset>

          {/* Row disposition */}
          <fieldset className="border border-coach-silver rounded-sm p-3">
            <legend className="text-[10.5px] uppercase tracking-widest text-steel px-1">Row disposition</legend>
            <select value={p.status || "READY"} onChange={(e) => setP({ ...p, status: e.target.value })}
                    data-testid={`edit-row-status-${p.source_row}`}
                    className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[13px] bg-white">
              {STATUS_ORDER.map((s) => <option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
            </select>
            <label className="block mt-2">
              <span className="text-[10.5px] uppercase tracking-widest text-steel">Operator note</span>
              <textarea value={p.operator_note || ""} rows={2}
                        onChange={(e) => setP({ ...p, operator_note: e.target.value })}
                        data-testid={`edit-row-note-${p.source_row}`}
                        className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[13px]" />
            </label>
          </fieldset>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="border border-coach-silver text-steel text-[12px] px-3 py-1.5 rounded-sm">Cancel</button>
            <button onClick={save} disabled={saving} data-testid={`edit-row-save-${p.source_row}`}
                    className="bg-coach-blue text-white text-[12px] px-3 py-1.5 rounded-sm hover:bg-deep-road-navy disabled:opacity-60">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Side-by-side existing vs incoming for POSSIBLE_DUPLICATE contact rows.
function DuplicateResolver({ proposal, dupContact, disabled, onAction }) {
  const incoming = dupContact;
  const existingId = dupContact.existing_id;
  const [existing, setExisting] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { api } = await import("@/lib/api");
        const r = await api.get(`/contacts/${existingId}`);
        if (!cancelled) setExisting(r.data);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [existingId]);

  const rows = [
    ["First name", "first_name"],
    ["Last name",  "last_name"],
    ["Primary email", "primary_email"],
    ["Mobile", "mobile"],
    ["Contact type / role", "role_in_row"],
  ];
  const same = (a, b) => (a || "").toString().trim().toLowerCase() === (b || "").toString().trim().toLowerCase();

  return (
    <div className="border border-coach-silver bg-road-white rounded-sm p-3" data-testid="duplicate-resolver">
      <div className="flex items-center gap-1 text-carbon text-[11.5px] uppercase tracking-widest mb-2">
        <Copy size={12} /> Possible Duplicate — Contact
      </div>
      <table className="w-full text-[12px] border border-coach-silver">
        <thead className="bg-chrome-mist text-[10.5px] uppercase tracking-widest text-steel">
          <tr>
            <th className="text-left px-2 py-1 w-32">Field</th>
            <th className="text-left px-2 py-1">Incoming (row {proposal.source_row})</th>
            <th className="text-left px-2 py-1">Existing ({existingId?.slice(0,8)}…)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, key]) => {
            const inc = incoming?.[key] ?? "—";
            const ex = existing?.[key] ?? "—";
            const match = same(inc, ex);
            return (
              <tr key={key} className="border-t border-coach-silver/60" data-testid={`dup-field-${key}`}>
                <td className="px-2 py-1 text-steel">{label}</td>
                <td className={`px-2 py-1 ${match ? "text-carbon" : "bg-amber-hold/15 text-carbon font-medium"}`}>{String(inc || "—")}</td>
                <td className={`px-2 py-1 ${match ? "text-carbon" : "bg-amber-hold/15 text-carbon font-medium"}`}>{String(ex || "—")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="flex flex-wrap gap-2 mt-3">
        <button disabled={disabled} onClick={() => onAction("USE_EXISTING")}
                data-testid="dup-action-use-existing"
                className="bg-coach-blue text-white text-[11.5px] px-3 py-1.5 rounded-sm">Use Existing</button>
        <button disabled={disabled} onClick={() => onAction("UPDATE_EXISTING")}
                data-testid="dup-action-update-existing"
                className="border border-coach-blue text-coach-blue text-[11.5px] px-3 py-1.5 rounded-sm">Update Existing</button>
        <button disabled={disabled} onClick={() => onAction("CREATE_NEW")}
                data-testid="dup-action-create-new"
                className="border border-amber-hold text-amber-hold text-[11.5px] px-3 py-1.5 rounded-sm">Create New</button>
        <button disabled={disabled} onClick={() => onAction("SKIP")}
                data-testid="dup-action-skip"
                className="border border-coach-silver text-steel text-[11.5px] px-3 py-1.5 rounded-sm">Skip</button>
      </div>
    </div>
  );
}
